/**
 * Менеджер визуализации CSElement
 */

import { EventEmitter } from 'eventemitter3';
import { ICSElement } from '../types/interfaces';
import {
  VisualizationManager as IVisualizationManager,
  VisualizationEngine,
  VisualizationGraph,
  VisualizationNode,
  VisualizationEdge,
  VisualizationOptions,
  VisualizationResult,
  VisualizationFormat,
  VisualizationEvent,

  VisualizationTheme
} from '../types/visualization-interfaces';

export class VisualizationManager extends EventEmitter implements IVisualizationManager {
  private engines: Map<string, VisualizationEngine> = new Map();
  private themes: Map<string, VisualizationTheme> = new Map();

  constructor() {
    super();
    this.initializeDefaultThemes();
  }

  /**
   * Инициализация стандартных тем
   */
  private initializeDefaultThemes(): void {
    // Светлая тема
    this.themes.set('light', {
      name: 'light',
      backgroundColor: '#ffffff',
      colors: {
        primary: '#2563eb',
        secondary: '#64748b',
        accent: '#f59e0b',
        text: '#1f2937',
        border: '#d1d5db'
      },
      nodeStyle: {
        backgroundColor: '#f8fafc',
        borderColor: '#d1d5db',
        borderWidth: 1,
        textColor: '#1f2937',
        fontSize: 12,
        shape: 'rectangle',
        padding: 8,
        borderRadius: 4
      },
      edgeStyle: {
        color: '#6b7280',
        width: 1,
        style: 'solid',
        arrowType: 'arrow'
      }
    });

    // Темная тема
    this.themes.set('dark', {
      name: 'dark',
      backgroundColor: '#111827',
      colors: {
        primary: '#3b82f6',
        secondary: '#9ca3af',
        accent: '#fbbf24',
        text: '#f9fafb',
        border: '#374151'
      },
      nodeStyle: {
        backgroundColor: '#1f2937',
        borderColor: '#374151',
        borderWidth: 1,
        textColor: '#f9fafb',
        fontSize: 12,
        shape: 'rectangle',
        padding: 8,
        borderRadius: 4
      },
      edgeStyle: {
        color: '#9ca3af',
        width: 1,
        style: 'solid',
        arrowType: 'arrow'
      }
    });

    // Минималистичная тема
    this.themes.set('minimal', {
      name: 'minimal',
      backgroundColor: '#ffffff',
      colors: {
        primary: '#000000',
        secondary: '#666666',
        accent: '#000000',
        text: '#000000',
        border: '#cccccc'
      },
      nodeStyle: {
        backgroundColor: '#ffffff',
        borderColor: '#cccccc',
        borderWidth: 1,
        textColor: '#000000',
        fontSize: 11,
        shape: 'rectangle',
        padding: 6,
        borderRadius: 0
      },
      edgeStyle: {
        color: '#666666',
        width: 1,
        style: 'solid',
        arrowType: 'none'
      }
    });
  }

  /**
   * Зарегистрировать движок визуализации
   */
  registerEngine(engine: VisualizationEngine): void {
    this.engines.set(engine.name, engine);
    // console.log(`📊 Движок визуализации "${engine.name}" зарегистрирован`);
  }

  /**
   * Получить движок для формата
   */
  getEngine(format: VisualizationFormat): VisualizationEngine | null {
    for (const engine of this.engines.values()) {
      if (engine.supportsFormat(format)) {
        return engine;
      }
    }
    return null;
  }

  /**
   * Получить все движки
   */
  getEngines(): VisualizationEngine[] {
    return Array.from(this.engines.values());
  }

  /**
   * Получить тему по имени
   */
  getTheme(name: string): VisualizationTheme | null {
    return this.themes.get(name) || null;
  }

  /**
   * Добавить пользовательскую тему
   */
  addTheme(theme: VisualizationTheme): void {
    this.themes.set(theme.name, theme);
  }

  /**
   * Получить все темы
   */
  getThemes(): VisualizationTheme[] {
    return Array.from(this.themes.values());
  }

  /**
   * Создать граф из элемента
   */
  createGraph(element: ICSElement, options: VisualizationOptions = {}): VisualizationGraph {
    const {
      maxDepth = Infinity,
      filter,
      showData = false,
      showIds = false,
      showIndices = false
    } = options;

    const nodes: VisualizationNode[] = [];
    const edges: VisualizationEdge[] = [];
    const visited = new Set<string>();
    let maxLevelReached = 0;

    const traverse = (
      currentElement: ICSElement,
      level: number = 0,
      parentId?: string
    ): void => {
      if (level > maxDepth || visited.has(currentElement.id)) {
        return;
      }

      // Применяем фильтр если он есть
      if (filter && !filter(currentElement)) {
        return;
      }

      visited.add(currentElement.id);
      maxLevelReached = Math.max(maxLevelReached, level);

      // Создаем узел
      const label = this.createNodeLabel(currentElement, {
        showData,
        showIds,
        showIndices
      });

      const node: VisualizationNode = {
        id: currentElement.id,
        label,
        level,
        element: currentElement,
        data: {
          name: currentElement.name,
          index: currentElement.index,
          dataSize: currentElement.data.size,
          childrenCount: currentElement.elementsCount()
        }
      };

      nodes.push(node);

      // Создаем связь с родителем
      if (parentId) {
        const edge: VisualizationEdge = {
          id: `${parentId}-${currentElement.id}`,
          source: parentId,
          target: currentElement.id,
          type: 'parent-child'
        };
        edges.push(edge);
      }

      // Обходим дочерние элементы
      currentElement.getAllElements().forEach(child => {
        if (child instanceof Object && 'id' in child) {
          traverse(child as ICSElement, level + 1, currentElement.id);
        }
      });
    };

    traverse(element);

    return {
      nodes,
      edges,
      metadata: {
        rootId: element.id,
        maxDepth: maxLevelReached,
        totalNodes: nodes.length,
        totalEdges: edges.length
      }
    };
  }

  /**
   * Создать метку для узла
   */
  private createNodeLabel(
    element: ICSElement,
    options: {
      showData: boolean;
      showIds: boolean;
      showIndices: boolean;
    }
  ): string {
    const parts: string[] = [];

    // Основное имя
    parts.push(element.name || 'Unnamed');

    // ID
    if (options.showIds) {
      parts.push(`(${element.id.substring(0, 8)}...)`);
    }

    // Индекс
    if (options.showIndices) {
      parts.push(`[${element.index}]`);
    }

    // Данные
    if (options.showData && element.data.size > 0) {
      const dataEntries = Array.from(element.data.entries()).slice(0, 3);
      const dataStr = dataEntries
        .map(([key, value]) => `${key}: ${this.formatValue(value)}`)
        .join(', ');
      
      if (element.data.size > 3) {
        parts.push(`{${dataStr}, ...}`);
      } else {
        parts.push(`{${dataStr}}`);
      }
    }

    return parts.join(' ');
  }

  /**
   * Форматировать значение для отображения
   */
  private formatValue(value: any): string {
    if (typeof value === 'string') {
      return value.length > 20 ? `"${value.substring(0, 20)}..."` : `"${value}"`;
    }
    if (typeof value === 'number') {
      return value.toString();
    }
    if (typeof value === 'boolean') {
      return value.toString();
    }
    if (value === null) {
      return 'null';
    }
    if (value === undefined) {
      return 'undefined';
    }
    if (Array.isArray(value)) {
      return `Array(${value.length})`;
    }
    if (typeof value === 'object') {
      return 'Object';
    }
    return String(value);
  }

  /**
   * Применить тему к опциям
   */
  applyTheme(options: VisualizationOptions, themeName: string): VisualizationOptions {
    const theme = this.getTheme(themeName);
    if (!theme) {
      return options;
    }

    return {
      ...options,
      nodeStyle: {
        ...theme.nodeStyle,
        ...options.nodeStyle
      },
      edgeStyle: {
        ...theme.edgeStyle,
        ...options.edgeStyle
      }
    };
  }

  /**
   * Визуализировать элемент
   */
  async visualize(
    element: ICSElement,
    options: VisualizationOptions = {}
  ): Promise<VisualizationResult> {
    const startTime = Date.now();

    // Событие начала рендеринга
    const startEvent: VisualizationEvent = {
      type: 'render:start',
      element,
      options,
      timestamp: startTime
    };
    this.emit('render:start', startEvent);

    try {
      // Определяем формат по умолчанию
      const format = options.format || 'ascii';

      // Получаем движок для формата
      const engine = this.getEngine(format);
      if (!engine) {
        throw new Error(`Движок для формата "${format}" не найден`);
      }

      // Создаем граф
      const graph = this.createGraph(element, options);

      // Событие создания графа
      this.emit('graph:created', {
        type: 'graph:created',
        element,
        options,
        timestamp: Date.now()
      });

      // Применяем тему если указана
      let finalOptions = options;
      if (options.nodeStyle?.backgroundColor === undefined && options.edgeStyle?.color === undefined) {
        finalOptions = this.applyTheme(options, 'light');
      }

      // Рендерим граф
      const result = await engine.render(graph, finalOptions);

      // Добавляем время генерации
      result.metadata.generationTime = Date.now() - startTime;

      // Событие завершения рендеринга
      const completeEvent: VisualizationEvent = {
        type: 'render:complete',
        element,
        options: finalOptions,
        result,
        timestamp: Date.now()
      };
      this.emit('render:complete', completeEvent);

      return result;

    } catch (error) {
      // Событие ошибки
      const errorEvent: VisualizationEvent = {
        type: 'render:error',
        element,
        options,
        error: error as Error,
        timestamp: Date.now()
      };
      this.emit('render:error', errorEvent);

      throw error;
    }
  }

  /**
   * Получить статистику менеджера
   */
  getStats(): {
    enginesCount: number;
    themesCount: number;
    supportedFormats: VisualizationFormat[];
  } {
    const supportedFormats = new Set<VisualizationFormat>();
    
    for (const engine of this.engines.values()) {
      engine.supportedFormats.forEach(format => supportedFormats.add(format));
    }

    return {
      enginesCount: this.engines.size,
      themesCount: this.themes.size,
      supportedFormats: Array.from(supportedFormats)
    };
  }

  /**
   * Очистить все движки (для тестирования)
   */
  clear(): void {
    this.engines.clear();
    this.themes.clear();
    this.initializeDefaultThemes();
  }
} 